import { useState, useMemo, useEffect } from "react";
import {
  areObjectsEqual,
  focusElement,
  orderStatusList,
  inputOnEnter,
  userData,
  frameOrderStructure,
  genderProductOptions,
  selectedOptic,
} from "../js/utils";
import InputIconSearch from "./InputIconSearch";
import InputData from "./InputData";
import Dropdown from "./Dropdown";
import { OrderActionButtons } from "./OrderActionButtons";
import { post } from "../js/post";
import { useModelsTradeNamesAndProducts } from "../hooks/useModelsTradeNamesAndProducts";
import { useOrderBase } from "../hooks/useOrderBase";

export function FrameOrder({
  orderId,
  createPopupQuestion,
  removePopupQuestion,
  onSave,
  onClose,
}) {
  const {
    initialOrder,
    currentOrder,
    setCurrentOrder,
    currentOrderFixed,
    setCurrentOrderFixed,
    selectedSupplier,
    setSelectedSupplier,
    searchCode,
    setSearchCode,
    supplierList,
    brandList,
    validateOrder,
    focusFields,
    saveOrder,
    deleteOrder,
  } = useOrderBase({
    orderId,
    familia: 4,
    structure: frameOrderStructure,
    requiredFields: ["proveedor", "marca", "modelo"],
    fieldsToFocus: [
      { field: "proveedor", elementId: 51 },
      { field: "marca", elementId: 52 },
      { field: "modelo", elementId: 53 },
    ],
    saveEndpoint: "support-extra/pedidos/GuardarPedidoMontura.php",
    getOrderEndpoint: "support-extra/pedidos/ObtenerPedidoMontura.php",
    onSave,
    createPopupQuestion,
    removePopupQuestion,
  });
  const [materialList, setMaterialList] = useState([]);
  const [shapeList, setShapeList] = useState([]);
  const [rawModelsList] = useModelsTradeNamesAndProducts(
    [4],
    currentOrder.proveedor,
    currentOrder.marca !== "" ? [currentOrder.marca] : [],
  );
  const modelsList = useMemo(() => {
    return [
      { id: "", text: "" },
      ...rawModelsList.map((e) => ({
        id: e,
        text: e,
      })),
    ];
  }, [rawModelsList]);

  // console.log("currentOrder: ", currentOrder);
  // console.log("selectedSupplier: ", selectedSupplier);
  // console.log("brandList: ", brandList);

  async function getProduct() {
    const response = await post("ObtenerMontura.php", {
      token: userData.token,
      codigo: searchCode,
      tipoCodigo: -1,
      idOptica: selectedOptic.id,
    });
    // console.log(
    // "BUSCANDO MONTURA: ",
    // {
    // token: userData.token,
    // codigo: searchCode,
    // tipoCodigo: -1,
    // idOptica: selectedOptic.id,
    // },
    // response,
    // );
    if (response.id) {
      const productFields = {
        codigo: response.codigoOPTIWIN || response.codigoEANUPC,
        proveedor: response.idProveedor,
        marca: response.marca,
        modelo: response.modelo,
        genero: response.genero,
        color: response.color,
        calibre: response.calibre,
        puente: response.puente,
        material: response.material,
        varilla: response.varilla,
        forma: response.forma,
        refProveedor: response.referenciaProveedor,
      };
      setCurrentOrder((prev) => ({ ...prev, ...productFields }));
      setCurrentOrderFixed((prev) => ({ ...prev, ...productFields }));
      setSearchCode(productFields.codigo);
      const supplier = supplierList.find(
        (s) => s.id === parseInt(response.idProveedor),
      );
      if (supplier) setSelectedSupplier(supplier);
    } else {
      setCurrentOrder(initialOrder);
      setCurrentOrderFixed(initialOrder);
      setSelectedSupplier({ id: "", text: "" });
      createPopupQuestion(
        "Producto no encontrado.",
        async () => {
          focusElement(50);
          removePopupQuestion();
        },
        null,
        "Entendido",
      );
    }
  }

  async function getMaterials() {
    const response = await post("ListarMaterialesLenteOftalmica.php", {
      token: userData.token,
    });
    setMaterialList([
      { id: "", text: "" },
      ...response.map((e) => ({ id: e.descripcion, text: e.descripcion })),
    ]);
  }

  async function getShapes() {
    const response = await post("ListarFormasMontura.php", {
      token: userData.token,
    });
    setShapeList([
      { id: "", text: "" },
      ...response.map((e) => ({ id: e.descripcion, text: e.descripcion })),
    ]);
  }

  useEffect(() => {
    getMaterials();
    getShapes();
  }, []);

  return (
    <div className="popup-container">
      <div className="ap-container popup-screen">
        <div className="popup-screen-header">
          <span className="popup-screen-header-title">Pedido de monturas</span>
          <button
            className="popup-action-icon close-icon"
            onClick={() => {
              if (!areObjectsEqual(currentOrder, currentOrderFixed)) {
                createPopupQuestion(
                  "Existen cambios sin guardar. ¿Desea salir de la ficha de pedido?",
                  async () => {
                    removePopupQuestion();
                    onClose();
                  },
                  () => {
                    removePopupQuestion();
                  },
                  "Sí",
                  "No",
                );
              } else onClose();
            }}
          >
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
        </div>
        <div
          className="tab-content ap-content"
          style={{ overflow: "unset", backgroundColor: "#f9f9f9" }}
        >
          <div
            className="container-panel"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div className="opt-table-title">Datos del producto</div>
            <div className="opt-table-content grid-5" id="order-product-grid">
              <div className="cell cell-label cell-start-content">
                Código/EAN/UPC
              </div>
              <div className="cell">
                <InputIconSearch
                  tabIndex={50}
                  type="type-alphanum-ext"
                  size={0}
                  maxLength={10}
                  input={searchCode}
                  onChange={(e) => {
                    setSearchCode(e);
                  }}
                  onClear={() => {
                    focusElement(50);
                    setSearchCode("");
                  }}
                  onEnter={async () => {
                    if (searchCode) await getProduct();
                  }}
                ></InputIconSearch>
              </div>
              <div className="cell combined-cells-3"></div>
              <div className="cell cell-label cell-start-content">
                Proveedor (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={51}
                  optionList={supplierList}
                  size={0}
                  mandatory={true}
                  op={selectedSupplier.id}
                  onChange={(e) => {
                    // console.log("PROVEEDOR SELECCIONADO: ", e);
                    setSelectedSupplier(e);
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Marca (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={52}
                  optionList={brandList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.marca}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      marca: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Modelo (*)
              </div>
              <div className="cell">
                <Dropdown
                  tabIndex={53}
                  optionList={modelsList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.modelo}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      modelo: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Género</div>
              <div className="cell">
                <Dropdown
                  tabIndex={57}
                  optionList={genderProductOptions}
                  size={0}
                  mandatory={false}
                  op={currentOrder.genero}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      genero: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">Color</div>
              <div className="cell">
                <InputData
                  tabIndex={54}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.color}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      color: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Calibre x puente
              </div>
              <div className="cell">
                <div className="cell">
                  <InputData
                    tabIndex={58}
                    type="type-integer"
                    size={0}
                    input={currentOrder.calibre}
                    onChange={(e) => {
                      setCurrentOrder((prev) => ({
                        ...prev,
                        calibre: e,
                      }));
                    }}
                  ></InputData>
                  <span className="cell-label">x</span>
                  <InputData
                    tabIndex={59}
                    type="type-integer"
                    size={0}
                    input={currentOrder.puente}
                    onChange={(e) => {
                      setCurrentOrder((prev) => ({
                        ...prev,
                        puente: e,
                      }));
                    }}
                  ></InputData>
                </div>
              </div>
              <div className="cell cell-label cell-start-content">Material</div>
              <div className="cell">
                <Dropdown
                  tabIndex={55}
                  optionList={materialList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.material}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      material: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Varilla</div>
              <div className="cell">
                <InputData
                  tabIndex={60}
                  type="type-integer"
                  size={0}
                  input={currentOrder.varilla}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      varilla: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-label cell-start-content">Forma</div>
              <div className="cell">
                <Dropdown
                  tabIndex={56}
                  optionList={shapeList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.forma}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      forma: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Ref. proveedor
              </div>
              <div className="cell">
                <InputData
                  tabIndex={61}
                  maxLength={20}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.refProveedor}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      refProveedor: e,
                    }));
                  }}
                ></InputData>
              </div>
            </div>
            <div className="opt-table-title" style={{ marginTop: 20 }}>
              Datos del pedido:{" "}
              {currentOrder.idPedido === -1
                ? selectedOptic.nombre
                : currentOrder.nombreOptica}
            </div>
            <div className="opt-table-content grid-5" id="order-product-grid">
              <div className="cell cell-label cell-start-content">
                Fecha pedido
              </div>
              <div className="cell">
                <input
                  type="date"
                  max="9999-12-31"
                  tabIndex={72}
                  className="input-field input-cell"
                  value={currentOrder.fechaPedido}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      fechaPedido: e.target.value,
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Estado</div>
              <div className="cell">
                <Dropdown
                  tabIndex={74}
                  optionList={orderStatusList}
                  size={0}
                  op={currentOrder.estado}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      estado: e.id,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Fecha recepción
              </div>
              <div className="cell">
                <input
                  type="date"
                  max="9999-12-31"
                  tabIndex={73}
                  className="input-field input-cell"
                  value={currentOrder.fechaRecepcion}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      fechaRecepcion: e.target.value,
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Unidades</div>
              <div className="cell">
                <button
                  className="incdec-btn"
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidades:
                        parseInt(prev.unidades) >= 2
                          ? parseInt(prev.unidades) - 1
                          : parseInt(prev.unidades),
                    }));
                  }}
                >
                  -
                </button>
                <div style={{ flex: 1 }}>
                  <InputData
                    tabIndex={75}
                    type="type-integer"
                    size={0}
                    input={currentOrder.unidades}
                    onChange={(e) => {
                      setCurrentOrder((prev) => ({
                        ...prev,
                        unidades: e,
                      }));
                    }}
                  ></InputData>
                </div>
                <button
                  className="incdec-btn"
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidades: parseInt(prev.unidades) + 1,
                    }));
                  }}
                >
                  +
                </button>
              </div>
            </div>
            <OrderActionButtons
              validateOrder={validateOrder}
              focusFields={focusFields}
              saveOrder={saveOrder}
              deleteOrder={deleteOrder}
              onSave={onSave}
              onClose={onClose}
              idPedido={currentOrder.idPedido}
              createPopupQuestion={createPopupQuestion}
              removePopupQuestion={removePopupQuestion}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
